import type { MeasureFormula } from '../../../../../types/measure-formula.interface';

export type FormulaStatus =
  | 'active-since'
  | 'active-from'
  | 'suspended-since'
  | 'suspended-from'
  | 'expired-active'
  | 'expired-suspended';

export function isInactivePeriod(formula: MeasureFormula): boolean {
  return formula.formulaId == null && formula.formula == null;
}

export function findNextSuccessorStartMs(formula: MeasureFormula, formulas: readonly MeasureFormula[]): number {
  const formulaStartMs = new Date(formula.startTs).getTime();
  let nextStartMs = Infinity;
  for (const candidate of formulas) {
    if (candidate.id === formula.id) continue;
    const candStart = new Date(candidate.startTs).getTime();
    if (candStart > formulaStartMs && candStart < nextStartMs) nextStartMs = candStart;
  }
  return nextStartMs;
}

export function findInForceFormula(formulas: readonly MeasureFormula[], nowMs: number): MeasureFormula | undefined {
  return formulas.find(f => {
    const startMs = new Date(f.startTs).getTime();
    if (startMs > nowMs) return false;
    const endMs = f.endTs ? new Date(f.endTs).getTime() : null;
    return endMs == null || endMs > nowMs;
  });
}

export function computeFormulaStatuses(formulas: readonly MeasureFormula[], nowMs: number): Map<number, FormulaStatus> {
  const result = new Map<number, FormulaStatus>();
  for (const formula of formulas) {
    result.set(formula.id, computeStatus(formula, nowMs));
  }
  return result;
}

function computeStatus(formula: MeasureFormula, nowMs: number): FormulaStatus {
  const endMs = formula.endTs ? new Date(formula.endTs).getTime() : null;
  const isPast = endMs != null && endMs <= nowMs;
  if (isPast) return isInactivePeriod(formula) ? 'expired-suspended' : 'expired-active';
  const startMs = new Date(formula.startTs).getTime();
  const isFuture = startMs > nowMs;
  if (isInactivePeriod(formula)) return isFuture ? 'suspended-from' : 'suspended-since';
  return isFuture ? 'active-from' : 'active-since';
}

export interface OverwriteImpact {
  truncated: MeasureFormula[];
  split: MeasureFormula[];
  swallowed: MeasureFormula[];
}

// Mirrors the backend painter: a new segment [newStartMs, newEndMs) truncates any sibling it
// partially overlaps on one edge, splits one it overlaps on both edges, and swallows one it
// fully covers. Used to preview the impact before submitting a free-scheduled segment.
export function computeOverwriteImpact(
  segments: readonly MeasureFormula[],
  newStartMs: number,
  newEndMs: number | null,
): OverwriteImpact {
  const endEff = newEndMs ?? Infinity;
  const impact: OverwriteImpact = { truncated: [], split: [], swallowed: [] };
  for (const seg of segments) {
    const segStart = new Date(seg.startTs).getTime();
    const segEnd = seg.endTs ? new Date(seg.endTs).getTime() : Infinity;
    if (segEnd <= newStartMs || segStart >= endEff) continue;
    const coversLeft = segStart < newStartMs;
    const coversRight = segEnd > endEff;
    if (coversLeft && coversRight) impact.split.push(seg);
    else if (coversLeft || coversRight) impact.truncated.push(seg);
    else impact.swallowed.push(seg);
  }
  return impact;
}

export function hasOverwriteImpact(impact: OverwriteImpact): boolean {
  return impact.truncated.length > 0 || impact.split.length > 0 || impact.swallowed.length > 0;
}

// Mirrors the backend painter's ``insert_segment``: an open-ended suspension (no explicit end)
// caps at the next surviving segment's start rather than reaching to infinity. Used to bound the
// overwrite preview so it doesn't flag a later segment as swallowed when the API would actually
// leave it standing.
export function findNextSegmentStartMs(segments: readonly MeasureFormula[], afterMs: number): number | null {
  let next: number | null = null;
  for (const seg of segments) {
    const startMs = new Date(seg.startTs).getTime();
    if (startMs > afterMs && (next === null || startMs < next)) next = startMs;
  }
  return next;
}

export interface OverwriteConfirmationPort {
  ask: (
    messageKey: string,
    headerKey: string,
    params?: Record<string, unknown>,
    icon?: string,
    acceptLabelKey?: string,
  ) => Promise<boolean>;
  translate: (key: string, params?: Record<string, unknown>) => string;
}

export function formatShortLocalDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Renders the overwrite confirmation's `{{details}}` interpolation: one clause per non-empty
// category, each listing the affected segments' start dates in the user's locale.
export function describeOverwriteImpact(
  impact: OverwriteImpact,
  translate: (key: string, params?: Record<string, unknown>) => string,
): string {
  const dateList = (segments: readonly MeasureFormula[]): string => segments.map(s => formatShortLocalDate(s.startTs)).join(', ');
  const parts: string[] = [];
  if (impact.truncated.length > 0) {
    parts.push(translate('entities.measureFormula.confirmations.overwriteTruncated', { dates: dateList(impact.truncated) }));
  }
  if (impact.split.length > 0) {
    parts.push(translate('entities.measureFormula.confirmations.overwriteSplit', { dates: dateList(impact.split) }));
  }
  if (impact.swallowed.length > 0) {
    parts.push(translate('entities.measureFormula.confirmations.overwriteSwallowed', { dates: dateList(impact.swallowed) }));
  }
  return parts.join(' ; ');
}

// Shared gate for both creation paths (new formula, new suspension): previews the painter's
// cascade and lets the user back out before any API call fires. Returns true when nothing is
// affected (no prompt needed) or once the user confirms the prompt.
export async function confirmOverwriteIfNeeded(
  port: OverwriteConfirmationPort,
  segments: readonly MeasureFormula[],
  newStartMs: number,
  newEndMs: number | null,
): Promise<boolean> {
  const impact = computeOverwriteImpact(segments, newStartMs, newEndMs);
  if (!hasOverwriteImpact(impact)) return true;

  const details = describeOverwriteImpact(impact, port.translate);
  return port.ask(
    'entities.measureFormula.confirmations.overwrite',
    'entities.measureFormula.confirmations.overwriteTitle',
    { details },
    'caution',
    'entities.measureFormula.messages.overwriteConfirm',
  );
}
